import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSuggestions } from '../utils/mealSuggestions';
import IngredientAutocomplete from './IngredientAutocomplete';

export const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
export const MEAL_TYPES = ['lunch', 'dinner'];

export function computeIngredientAvailability(meals, pantry) {
  const pantryHave = {};
  const pantryArr = Array.isArray(pantry) ? pantry : [];
  for (const item of pantryArr) {
    if (item.name) {
      pantryHave[item.name.toLowerCase().trim()] = item.qty || 0;
    }
  }
  const remaining = { ...pantryHave };
  const availability = {};
  for (const day of DAYS) {
    for (const mealType of MEAL_TYPES) {
      const slots = meals[day]?.[mealType];
      const slotsArr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
      for (let i = 0; i < slotsArr.length; i++) {
        const meal = slotsArr[i];
        const key = `${day}-${mealType}-${i}`;
        availability[key] = {};
        const ingredients = Array.isArray(meal?.ingredients) ? meal.ingredients : [];
        for (const ing of ingredients) {
          if (ing.name) {
            const ingKey = ing.name.toLowerCase().trim();
            availability[key][ingKey] = remaining[ingKey] ?? 0;
            remaining[ingKey] = (remaining[ingKey] ?? 0) - (parseFloat(ing.quantity) || 0);
          }
        }
      }
    }
  }
  return availability;
}

export function MealSlot({ day, mealType, slotIndex = 0, meal, pantry, recipes, recentWeeks, currentWeekMeals, onSave, onAddCourse, onRemove, onAddRecipe, onAddToPantry, ingredientAvailability, isDragging, isDragOver, dragHandleProps, dropTargetProps }) {
  const { t } = useTranslation();
  const pantryList = (pantry || []).map((p) => p.name);
  const pantryNames = new Set(pantryList.map((n) => n.toLowerCase().trim()));
  const [name, setName] = useState(meal.name || '');
  const [ingredients, setIngredients] = useState(
    meal.ingredients.length > 0 ? meal.ingredients.map((ing) => ({ ...ing })) : []
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(false);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [recipeNote, setRecipeNote] = useState('');
  const [recipeSaveError, setRecipeSaveError] = useState(false);
  const [recipeSaving, setRecipeSaving] = useState(false);
  const [recipeSaveServings, setRecipeSaveServings] = useState('');
  const [showServingsDialog, setShowServingsDialog] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState(null);
  const [desiredServings, setDesiredServings] = useState('');

  const smartSuggestions = useMemo(() => getSuggestions({
    recipes,
    pantryItems: pantry,
    currentWeekMeals,
    recentWeeks,
  }), [recipes, pantry, currentWeekMeals, recentWeeks]);

  const hasAnySuggestions = smartSuggestions.pantryBased.length > 0 ||
    smartSuggestions.favorites.length > 0 ||
    smartSuggestions.rotation.length > 0;
  const nameInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const saveTimeout = useRef(null);
  const editCountRef = useRef(0);
  const savedCountRef = useRef(0);
  const qtyRefs = useRef([]);

  useEffect(() => {
    if (editCountRef.current !== savedCountRef.current) return;
    setName(meal.name || '');
    setIngredients(
      meal.ingredients.length > 0 ? meal.ingredients.map((ing) => ({ ...ing })) : []
    );
  }, [meal]);

  const scheduleSave = useCallback(
    (newName, newIngredients) => {
      editCountRef.current += 1;
      clearTimeout(saveTimeout.current);
      const capturedCount = editCountRef.current;
      saveTimeout.current = setTimeout(() => {
        savedCountRef.current = capturedCount;
        onSave(day, mealType, slotIndex, { name: newName, ingredients: newIngredients });
        saveTimeout.current = null;
      }, 600);
    },
    [day, mealType, onSave]
  );

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    scheduleSave(val, toNumeric(ingredients));

    // Filter recipes for autocomplete
    if (val.trim().length > 0 && recipes && recipes.length > 0) {
      const query = val.toLowerCase().trim();
      const matches = recipes
        .filter((r) => r.name.toLowerCase().includes(query))
        .sort((a, b) => {
          if (a.favorite && !b.favorite) return -1;
          if (!a.favorite && b.favorite) return 1;
          return 0;
        });
      setFilteredRecipes(matches);
      setShowSuggestions(matches.length > 0);
      setShowSmartSuggestions(false);
    } else {
      setShowSuggestions(false);
      setFilteredRecipes([]);
      if (!val.trim() && hasAnySuggestions) {
        setShowSmartSuggestions(true);
      } else {
        setShowSmartSuggestions(false);
      }
    }
  };

  const applyRecipe = (recipe, targetServings) => {
    setName(recipe.name);
    let newIngredients;
    if (targetServings && recipe.servings && targetServings !== recipe.servings) {
      const scale = targetServings / recipe.servings;
      newIngredients = recipe.ingredients.map((ing) => ({
        ...ing,
        quantity: Math.round(ing.quantity * scale * 100) / 100,
      }));
    } else {
      newIngredients = recipe.ingredients.map((ing) => ({ ...ing }));
    }
    setIngredients(newIngredients);
    setShowSuggestions(false);
    setShowSmartSuggestions(false);
    setFilteredRecipes([]);
    clearTimeout(saveTimeout.current);
    savedCountRef.current = editCountRef.current;
    onSave(day, mealType, slotIndex, { name: recipe.name, ingredients: newIngredients });
  };

  const selectRecipe = (recipe) => {
    if (recipe.servings) {
      setPendingRecipe(recipe);
      setDesiredServings(String(recipe.servings));
      setShowServingsDialog(true);
      setShowSuggestions(false);
      setShowSmartSuggestions(false);
    } else {
      applyRecipe(recipe, null);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        nameInputRef.current &&
        !nameInputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
        setShowSmartSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toNumeric = (ings) =>
    ings.map((ing) => ({ ...ing, quantity: parseFloat(ing.quantity) || 0 }));

  const handleIngredientChange = (index, field, value) => {
    const updated = ingredients.map((ing, i) =>
      i === index ? { ...ing, [field]: value } : ing
    );
    setIngredients(updated);
    scheduleSave(name, toNumeric(updated));
  };

  const addIngredient = () => {
    const updated = [...ingredients, { name: '', quantity: 1 }];
    setIngredients(updated);
    scheduleSave(name, toNumeric(updated));
  };

  const removeIngredient = (index) => {
    const updated = ingredients.filter((_, i) => i !== index);
    setIngredients(updated);
    scheduleSave(name, toNumeric(updated));
  };

  return (
    <div
      className={`meal-slot${isDragging ? ' dragging' : ''}${isDragOver ? ' drag-over' : ''}`}
      data-meal-loc={`${day}-${mealType}-${slotIndex}`}
      {...(dropTargetProps || {})}
    >
      <div className="meal-slot-header">
        {slotIndex === 0 ? (
          <>
            <span className={`meal-type-label ${mealType}`}>
              {mealType === 'lunch' ? '☀️' : '🌙'} {t(`week.${mealType}`)}
            </span>
            {onAddCourse && name.trim() && (
              <button type="button" className="add-course-btn" onClick={onAddCourse} title={t('week.addCourseTitle')}>
                {t('week.addCourse')}
              </button>
            )}
          </>
        ) : (
          <>
            <span className="course-label">{t('week.courseLabel', { number: slotIndex + 1 })}</span>
            {onRemove && (
              <button type="button" className="remove-course-btn" onClick={onRemove} title={t('week.removeCourseTitle')}>
                {t('week.removeCourse')}
              </button>
            )}
          </>
        )}
      </div>
      <div className="meal-name-wrapper">
        {name.trim() && dragHandleProps && (
          <span className="drag-handle" {...dragHandleProps}>⠿</span>
        )}
        <input
          ref={nameInputRef}
          className="meal-name-input"
          type="text"
          placeholder={t('week.mealPlaceholder')}
          value={name}
          onChange={handleNameChange}
          onFocus={() => {
            if (name.trim() && filteredRecipes.length > 0) {
              setShowSuggestions(true);
            } else if (!name.trim() && hasAnySuggestions) {
              setShowSmartSuggestions(true);
            }
          }}
        />
        {showSuggestions && filteredRecipes.length > 0 && (
          <div className="recipe-suggestions" ref={suggestionsRef}>
            {filteredRecipes.map((r) => (
              <div
                key={r.id}
                className="recipe-suggestion-item"
                onMouseDown={() => selectRecipe(r)}
              >
                <span className="recipe-suggestion-name">{r.favorite ? '★ ' : ''}{r.name}</span>
                <span className="recipe-suggestion-count">
                  {t('week.ingredients', { count: r.ingredients.length })}
                </span>
              </div>
            ))}
          </div>
        )}
        {showSmartSuggestions && hasAnySuggestions && (
          <div className="recipe-suggestions smart-suggestions" ref={suggestionsRef}>
            {smartSuggestions.pantryBased.length > 0 && (
              <>
                <div className="suggestion-section-header">{t('week.useWhatYouHave')}</div>
                {smartSuggestions.pantryBased.map((r) => (
                  <div key={r.id} className="recipe-suggestion-item" onMouseDown={() => selectRecipe(r)}>
                    <span className="recipe-suggestion-name">{r.name}</span>
                    <span className="recipe-suggestion-count">{t('week.inStock', { inStock: r.inStock, total: r.totalIngredients })}</span>
                  </div>
                ))}
              </>
            )}
            {smartSuggestions.favorites.length > 0 && (
              <>
                <div className="suggestion-section-header">{t('week.yourFavorites')}</div>
                {smartSuggestions.favorites.map((f, i) => (
                  <div key={i} className="recipe-suggestion-item" onMouseDown={() => {
                    if (f.recipe) { selectRecipe(f.recipe); }
                    else {
                      setName(f.name);
                      setShowSmartSuggestions(false);
                      scheduleSave(f.name, toNumeric(ingredients));
                    }
                  }}>
                    <span className="recipe-suggestion-name">{f.name}</span>
                    <span className="recipe-suggestion-count">{t('week.cooked', { count: f.count })}</span>
                  </div>
                ))}
              </>
            )}
            {smartSuggestions.rotation.length > 0 && (
              <>
                <div className="suggestion-section-header">{t('week.tryAgain')}</div>
                {smartSuggestions.rotation.map((r) => (
                  <div key={r.id} className="recipe-suggestion-item" onMouseDown={() => selectRecipe(r)}>
                    <span className="recipe-suggestion-name">{r.name}</span>
                    <span className="recipe-suggestion-count">{t('week.ingredients', { count: r.ingredients.length })}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      <div className="inline-ingredients">
        {ingredients.map((ing, index) => (
          <div key={index} className="inline-ingredient-row">
            <IngredientAutocomplete
              value={ing.name}
              pantryList={pantryList}
              placeholder={t('week.ingredient')}
              onChange={(val) => handleIngredientChange(index, 'name', val)}
              onAccept={() => {
                const el = qtyRefs.current[index];
                if (el) setTimeout(() => el.focus(), 0);
              }}
              t={t}
            />
            <input
              ref={(el) => (qtyRefs.current[index] = el)}
              className={`inline-qty${ing.name && ingredientAvailability && (() => {
                const ingKey = ing.name.toLowerCase().trim();
                const avail = ingredientAvailability[ingKey];
                return avail !== undefined && avail < (parseFloat(ing.quantity) || 0) ? ' ing-shortage' : '';
              })() || ''}`}
              type="text"
              inputMode="decimal"
              value={ing.quantity}
              onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
            />
            {(() => {
              if (!ing.name) return <span className="ing-status-placeholder" />;
              const ingKey = ing.name.toLowerCase().trim();
              const avail = ingredientAvailability?.[ingKey];
              if (!pantryNames.has(ingKey)) {
                return <button type="button" className="ing-not-in-pantry" title={t('week.notInPantryTitle')} onClick={() => onAddToPantry && onAddToPantry(ing.name.trim())}>❓</button>;
              }
              if (avail !== undefined && avail < (parseFloat(ing.quantity) || 0)) {
                return <span className="ing-warning ing-warning--shortage" title={t('week.shortageTitle', { amount: Math.round(Math.max(0, avail) * 100) / 100 })}>⚠️</span>;
              }
              return <span className="ing-status-placeholder" />;
            })()}
            <button
              type="button"
              className="inline-remove-btn"
              onClick={() => removeIngredient(index)}
              title="Remove"
            >
              ✕
            </button>
          </div>
        ))}
        {name.trim() && (
          <button type="button" className="inline-add-btn" onClick={addIngredient}>
            {t('week.addIngredient')}
          </button>
        )}
      </div>
      {name.trim() && ingredients.length > 0 && !recipes?.some(r => r.name.toLowerCase() === name.toLowerCase().trim()) && (
        <button
          type="button"
          className="save-as-recipe-btn"
          onClick={() => { setRecipeNote(''); setRecipeSaveServings(''); setRecipeSaveError(false); setShowNoteDialog(true); }}
        >
          {t('week.saveAsRecipe')}
        </button>
      )}
      {showNoteDialog && (
        <div className="note-dialog-overlay" onClick={() => setShowNoteDialog(false)}>
          <div className="note-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{t('week.saveRecipeTitle', { name })}</h3>
            <textarea
              className="note-dialog-textarea"
              placeholder={t('week.notesPlaceholder')}
              value={recipeNote}
              onChange={(e) => setRecipeNote(e.target.value)}
              autoFocus
            />
            <div className="recipe-servings-row">
              <label className="recipe-servings-label">{t('recipes.servings')}</label>
              <input
                className="recipe-servings-input"
                type="number"
                inputMode="numeric"
                min="1"
                max="100"
                placeholder={t('recipes.servingsPlaceholder')}
                value={recipeSaveServings}
                onChange={(e) => setRecipeSaveServings(e.target.value)}
              />
            </div>
            <div className="note-dialog-actions">
              {recipeSaveError && <p className="note-dialog-error">{t('week.failedToSave')}</p>}
              <button
                type="button"
                className="recipe-save-btn"
                disabled={recipeSaving}
                onClick={async () => {
                  setRecipeSaveError(false);
                  setRecipeSaving(true);
                  const ok = await onAddRecipe({
                    name: name.trim(),
                    ingredients: ingredients
                      .filter((ing) => ing.name.trim())
                      .map((ing) => ({ ...ing, quantity: parseFloat(ing.quantity) || 0 })),
                    notes: recipeNote,
                    servings: recipeSaveServings ? parseInt(recipeSaveServings, 10) || null : null,
                  });
                  setRecipeSaving(false);
                  if (ok) {
                    setShowNoteDialog(false);
                  } else {
                    setRecipeSaveError(true);
                  }
                }}
              >
                {recipeSaving ? t('week.saving') : t('week.save')}
              </button>
              <button type="button" className="recipe-cancel-btn" onClick={() => setShowNoteDialog(false)}>
                {t('week.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showServingsDialog && pendingRecipe && (
        <div className="note-dialog-overlay" onClick={() => { setShowServingsDialog(false); setPendingRecipe(null); }}>
          <div className="note-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{pendingRecipe.name}</h3>
            <p className="servings-dialog-info">
              {t('week.recipeServingsInfo', { count: pendingRecipe.servings })}
            </p>
            <div className="recipe-servings-row">
              <label className="recipe-servings-label">{t('week.cookingForHow')}</label>
              <input
                className="recipe-servings-input"
                type="number"
                inputMode="numeric"
                min="1"
                max="100"
                value={desiredServings}
                onChange={(e) => setDesiredServings(e.target.value)}
                autoFocus
              />
            </div>
            <div className="note-dialog-actions">
              <button
                type="button"
                className="recipe-save-btn"
                onClick={() => {
                  const target = parseInt(desiredServings, 10) || pendingRecipe.servings;
                  applyRecipe(pendingRecipe, target);
                  setShowServingsDialog(false);
                  setPendingRecipe(null);
                }}
              >
                {t('week.applyRecipe')}
              </button>
              <button type="button" className="recipe-cancel-btn" onClick={() => { setShowServingsDialog(false); setPendingRecipe(null); }}>
                {t('week.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WeekView({ meals, pantry, recipes, recentWeeks, dates, onSave, onAddSlot, onRemoveSlot, onAddRecipe, onAddToPantry, onMoveMeal }) {
  const { i18n } = useTranslation();
  const todayStr = new Date().toISOString().slice(0, 10);
  const availability = useMemo(() => computeIngredientAvailability(meals, pantry), [meals, pantry]);

  const [collapsedDays, setCollapsedDays] = useState(() => {
    const initial = {};
    DAYS.forEach((day, i) => {
      const dateStr = dates[i].toISOString().slice(0, 10);
      if (dateStr < todayStr) initial[day] = true;
    });
    return initial;
  });

  const toggleCollapse = (day) => {
    setCollapsedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  // Scroll to today's card on mount, accounting for sticky header height
  const todayRef = useRef(null);
  useEffect(() => {
    if (!todayRef.current) return;
    const stickyHeader = document.querySelector('.app-topbar');
    const stickyNav = document.querySelector('.app-nav');
    const offset = (stickyHeader?.offsetHeight || 0) + (stickyNav?.offsetHeight || 0);
    const top = todayRef.current.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Drag and drop state ---
  const [dragSource, setDragSource] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const weekGridRef = useRef(null);
  const onMoveMealRef = useRef(onMoveMeal);
  useEffect(() => { onMoveMealRef.current = onMoveMeal; }, [onMoveMeal]);

  // Desktop drag handlers
  const handleDragStart = useCallback((e, day, mealType, slotIndex) => {
    setDragSource({ day, mealType, slotIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
    const slotEl = e.target.closest('[data-meal-loc]');
    if (slotEl) {
      const rect = slotEl.getBoundingClientRect();
      e.dataTransfer.setDragImage(slotEl, e.clientX - rect.left, e.clientY - rect.top);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragSource(null);
    setDragOverTarget(null);
  }, []);

  const handleSlotDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const loc = e.currentTarget.dataset.mealLoc;
    if (loc) {
      const [d, mt, idx] = loc.split('-');
      setDragOverTarget({ day: d, mealType: mt, slotIndex: parseInt(idx) });
    }
  }, []);

  const handleSlotDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTarget(null);
    }
  }, []);

  const handleSlotDrop = useCallback((e) => {
    e.preventDefault();
    const loc = e.currentTarget.dataset.mealLoc;
    setDragSource((src) => {
      if (!src || !loc) return null;
      const [d, mt, idx] = loc.split('-');
      const target = { day: d, mealType: mt, slotIndex: parseInt(idx) };
      if (src.day !== target.day || src.mealType !== target.mealType || src.slotIndex !== target.slotIndex) {
        onMoveMealRef.current(src, target);
      }
      return null;
    });
    setDragOverTarget(null);
  }, []);

  // Touch drag for mobile
  useEffect(() => {
    const grid = weekGridRef.current;
    if (!grid) return;

    let timer = null;
    let active = false;
    let source = null;
    let ghost = null;
    let currentTargetEl = null;

    const onTouchStart = (e) => {
      const handle = e.target.closest('.drag-handle');
      if (!handle) return;
      const locEl = handle.closest('[data-meal-loc]');
      if (!locEl) return;
      const [day, mealType, idx] = locEl.dataset.mealLoc.split('-');
      const touch = e.touches[0];
      const startX = touch.clientX;
      const startY = touch.clientY;
      source = { day, mealType, slotIndex: parseInt(idx) };

      const moveCheck = (me) => {
        const t = me.touches[0];
        if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) {
          clearTimeout(timer);
          timer = null;
          grid.removeEventListener('touchmove', moveCheck);
        }
      };
      grid.addEventListener('touchmove', moveCheck, { passive: true });

      timer = setTimeout(() => {
        grid.removeEventListener('touchmove', moveCheck);
        active = true;
        locEl.classList.add('dragging');
        const mealName = locEl.querySelector('.meal-name-input')?.value || '';
        ghost = document.createElement('div');
        ghost.className = 'touch-drag-ghost';
        ghost.textContent = mealName;
        ghost.style.left = `${startX}px`;
        ghost.style.top = `${startY - 40}px`;
        document.body.appendChild(ghost);
        if (navigator.vibrate) navigator.vibrate(50);
      }, 400);
    };

    const onTouchMove = (e) => {
      if (!active) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (ghost) {
        ghost.style.left = `${touch.clientX}px`;
        ghost.style.top = `${touch.clientY - 40}px`;
      }
      if (ghost) ghost.style.display = 'none';
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghost) ghost.style.display = '';
      const targetEl = el?.closest?.('[data-meal-loc]');
      if (currentTargetEl && currentTargetEl !== targetEl) {
        currentTargetEl.classList.remove('drag-over');
      }
      if (targetEl) {
        targetEl.classList.add('drag-over');
        currentTargetEl = targetEl;
      } else {
        currentTargetEl = null;
      }
    };

    const onTouchEnd = () => {
      clearTimeout(timer);
      timer = null;
      if (active && source && currentTargetEl) {
        const [d, mt, idx] = currentTargetEl.dataset.mealLoc.split('-');
        const target = { day: d, mealType: mt, slotIndex: parseInt(idx) };
        if (source.day !== target.day || source.mealType !== target.mealType || source.slotIndex !== target.slotIndex) {
          onMoveMealRef.current(source, target);
        }
      }
      if (ghost) { ghost.remove(); ghost = null; }
      grid.querySelectorAll('.dragging').forEach((el) => el.classList.remove('dragging'));
      grid.querySelectorAll('.drag-over').forEach((el) => el.classList.remove('drag-over'));
      active = false;
      source = null;
      currentTargetEl = null;
    };

    grid.addEventListener('touchstart', onTouchStart, { passive: true });
    grid.addEventListener('touchmove', onTouchMove, { passive: false });
    grid.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      grid.removeEventListener('touchstart', onTouchStart);
      grid.removeEventListener('touchmove', onTouchMove);
      grid.removeEventListener('touchend', onTouchEnd);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="week-grid" ref={weekGridRef}>
      {DAYS.map((day, i) => {
        const dateStr = dates[i].toISOString().slice(0, 10);
        const isToday = dateStr === todayStr;
        const isPast = dateStr < todayStr;
        const collapsed = !!collapsedDays[day];
        const dayNum = dates[i].getUTCDate();
        const label = dates[i].toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
        const isFullyPlanned = MEAL_TYPES.every((mt) => {
          const slots = meals[day]?.[mt];
          const arr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
          return arr.some((s) => s.name && s.name.trim());
        });
        return (
          <div key={day} ref={isToday ? todayRef : undefined} className={`day-card${isPast ? ' past-day' : ''}${collapsed ? ' collapsed' : ''}`}>
            <div className="day-card-header" onClick={() => toggleCollapse(day)} style={{ cursor: 'pointer' }}>
              <h2>
                <span className="collapse-indicator">{collapsed ? '▸' : '▾'}</span>
                {label}
                {isFullyPlanned && <span className="planned-badge" aria-label="Fully planned">✓</span>}
              </h2>
              {collapsed && (
                <div className="collapsed-meals-summary">
                  {MEAL_TYPES.map((mealType) => {
                    const slots = meals[day]?.[mealType];
                    const slotsArr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
                    const names = slotsArr.map((s) => s.name).filter(Boolean);
                    return names.length > 0 ? (
                      <span key={mealType} className="collapsed-meal-name">
                        {mealType === 'lunch' ? '☀️' : '🌙'} {names.join(', ')}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            {!collapsed && (
              <div className="day-card-body">
                {MEAL_TYPES.map((mealType) => {
                  const slots = meals[day]?.[mealType];
                  const slotsArr = Array.isArray(slots) ? slots : (slots ? [slots] : [{ name: '', ingredients: [] }]);
                  return (
                    <div key={mealType} className="meal-type-slots">
                      {slotsArr.map((slot, i) => {
                        const isDragging = dragSource?.day === day && dragSource?.mealType === mealType && dragSource?.slotIndex === i;
                        const isDragOver = dragOverTarget?.day === day && dragOverTarget?.mealType === mealType && dragOverTarget?.slotIndex === i;
                        return (
                          <MealSlot
                            key={i}
                            day={day}
                            mealType={mealType}
                            slotIndex={i}
                            meal={slot}
                            pantry={pantry}
                            recipes={recipes}
                            recentWeeks={recentWeeks}
                            currentWeekMeals={meals}
                            onSave={onSave}
                            onAddCourse={i === 0 ? () => onAddSlot(day, mealType) : undefined}
                            onRemove={i > 0 ? () => onRemoveSlot(day, mealType, i) : undefined}
                            onAddRecipe={onAddRecipe}
                            onAddToPantry={onAddToPantry}
                            ingredientAvailability={availability[`${day}-${mealType}-${i}`]}
                            isDragging={isDragging}
                            isDragOver={isDragOver}
                            dragHandleProps={{
                              draggable: true,
                              onDragStart: (e) => handleDragStart(e, day, mealType, i),
                              onDragEnd: handleDragEnd,
                            }}
                            dropTargetProps={{
                              onDragOver: handleSlotDragOver,
                              onDragLeave: handleSlotDragLeave,
                              onDrop: handleSlotDrop,
                            }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default WeekView;
